import { Head, router } from '@inertiajs/react'
import {
    Box,
    Container,
    Typography,
    ThemeProvider
} from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon
} from '@mui/icons-material'
import { SecondaryButton, PrimaryButton, ProfileCard } from '../../components'
import theme from '../../styles/theme'
import { useAuth } from '../../hooks/useAuth'

interface ProfileData {
    // Basic Info
    first_name: string;
    last_initial: string;
    profile_pic_url: string | null;
    short_intro: string;
    prospectId: string;

    // Employment
    occupation: string;
    employer: string;

    // Rental Preferences
    budget: string;
    bedrooms: string;
    bathrooms: string;
    desired_move_in: string;
    lease_length: string;
    rental_type?: string;
    tour_preference?: string;
    occupants: string;
    neighborhoods: string;
    additionalInfo: string;

    // Pet and housing info
    housingVoucher: string;
    numDogs: string;
    numCats: string;
}

interface Props {
    profileData: ProfileData;
}

export default function ProfilePreview({ profileData }: Props) {
    const { renter } = useAuth();

    const handleGoBack = () => {
        router.visit(`/renters/${renter?.id}/home`)
    }

    const handleEditProfile = () => {
        router.visit(`/renters/${renter?.id}/profile/edit`)
    }

    return (
        <ThemeProvider theme={theme}>
            <Head title="Profile Preview - HeyyProperty" />

            <Box sx={{
                minHeight: '100vh',
                bgcolor: 'grey.50',
                py: 3
            }}>
                <Container maxWidth="md">
                    {/* Header */}
                    <Box sx={{
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1000,
                        bgcolor: 'grey.50',
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: 'grey.200'
                    }}>
                        <SecondaryButton
                            startIcon={<ArrowBackIcon />}
                            onClick={handleGoBack}
                        >
                            Back to Dashboard
                        </SecondaryButton>
                        <PrimaryButton
                            startIcon={<EditIcon />}
                            onClick={handleEditProfile}
                            sx={{
                                bgcolor: 'black',
                                '&:hover': { bgcolor: 'grey.800' }
                            }}
                        >
                            Edit Profile
                        </PrimaryButton>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            Profile Preview
                        </Typography>
                        <Typography variant="body1" color="grey.600">
                            This is how landlords see your profile<br />
                            <strong>Note:</strong> Your budget information is not shared with landlords.
                        </Typography>
                    </Box>

                    {/* Profile Card */}
                    <ProfileCard profileData={profileData} />

                </Container>
            </Box>
        </ThemeProvider>
    )
}
